/* @flow */
import * as React from 'react';
import { graphql } from 'react-apollo';

import ToastActions from 'actions/ToastActions';
import ConfirmDialog from 'components/ConfirmDialog';
import Copy from 'components/Copy';
import FormElement from 'components/FormElement';
import Row from 'components/Row';
import Textarea from 'components/Textarea';
import REMOVE_GROUP_FROM_ORG_MUTATION from 'components/PeopleList/queries/RemoveGroupFromOrgMutation.gql';
import type { ClassProfile_classes } from 'groups/queries/__generated__/ClassProfile';

type Props = {
  group: ClassProfile_classes,
  onRemoveGroupFromOrg: () => void,
  mutate: ({
    variables: {
      input: any,
    },
  }) => Promise<*>,
};

type State = {
  reason: string, // for the love of god don't set gql variables to null, sending empty string here to make service happy. - Tor 7/31/2019
};

class GroupRemoveFromOrgModal extends React.Component<Props, State> {
  state = {
    reason: '',
  };

  removeGroup = () => {
    const { mutate, group, onRemoveGroupFromOrg } = this.props;
    const { reason } = this.state;

    const { className, organization } = group;
    if (!organization) {
      return null;
    }

    return mutate({
      variables: {
        input: {
          groupUuid: group.uuid,
          organizationUuid: organization.uuid,
          reason,
        },
      },
    })
      .then(response => {
        if (response.error) {
          ToastActions.showErrorToast(
            `Something went wrong! ${className} was not removed from ${organization.name}`
          );
        } else {
          onRemoveGroupFromOrg();
          ToastActions.showSuccessToast(`${className} has been removed from ${organization.name}`);
        }
      })
      .catch(() => {
        // graphql will error with a 500 on a bad mutation not return 200.
        // keeping above error message for the respnse.error as 'just in case' code.
        ToastActions.showErrorToast(
          `Something went wrong! ${className} was not removed from ${organization.name}`
        );
      });
  };

  render() {
    const { group, ...others } = this.props;

    if (!group.organization) {
      return null;
    }

    return (
      <ConfirmDialog
        {...others}
        actionButtonText="Remove from school"
        cancelButtonText="Cancel"
        divided
        title={`Remove this class from ${group.organization.name}?`}
        size="small"
        onConfirm={this.removeGroup}>
        <Row>
          <Copy color="graphite">
            <p>When you remove {group.className},</p>
            <ul>
              <li>
                <p>
                  Participants who are not part of other classes in this school will also be
                  removed.
                </p>
              </li>
              <li>
                <p>Participants linked to your SIS will not be removed.</p>
              </li>
              <li>
                <p>
                  The class owner(s) will be notified by email, and you can add an optional message
                  below.
                </p>
              </li>
            </ul>
          </Copy>
        </Row>
        <FormElement
          label="Optional message to class owner(s)"
          element={Textarea}
          elementProps={{
            name: 'message',
            placeholder: 'Type your message here',
            value: this.state.reason,
          }}
          onValidChange={e => this.setState({ reason: e.target.value })}
        />
      </ConfirmDialog>
    );
  }
}

const withRemoveFromOrgMutation = graphql(REMOVE_GROUP_FROM_ORG_MUTATION);

export { GroupRemoveFromOrgModal };

export default withRemoveFromOrgMutation(GroupRemoveFromOrgModal);
